import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getIconComponent } from '@/utils/iconUtils';
import { cn } from '@/lib/utils';

export interface BadgePillProps {
  badge: {
    id: string;
    name: string;
    translatedName?: string;
    description?: string;
    icon: string;
    color: string;
    is_system_badge?: boolean;
  };
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  showNewIndicator?: boolean;
  className?: string;
}

export const BadgePill: React.FC<BadgePillProps> = ({
  badge,
  size = 'md',
  showTooltip = true,
  showNewIndicator = false,
  className = ''
}) => {
  const IconComponent = getIconComponent(badge.icon);
  const displayName = badge.translatedName || badge.name;

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 h-6',
    md: 'text-sm px-3 py-1.5 h-7',
    lg: 'text-base px-4 py-2 h-8'
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4', 
    lg: 'h-5 w-5'
  };

  const badgeContent = (
    <div className="relative inline-flex">
      <Badge
        className={cn(
          'inline-flex items-center gap-1.5 font-medium transition-all duration-200 hover:shadow-md border-0',
          sizeClasses[size],
          className
        )}
        style={{
          backgroundColor: `${badge.color}20`,
          color: badge.color,
          border: `1px solid ${badge.color}40`
        }}
      >
        <IconComponent 
          className={cn(iconSizes[size], 'flex-shrink-0')}
          style={{ color: badge.color }}
        />
        <span className="truncate max-w-32">{displayName}</span>
      </Badge>
      
      {showNewIndicator && (
        <div className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full border border-white" />
      )}
    </div>
  );

  if (!showTooltip || !badge.description) {
    return badgeContent;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-medium">{displayName}</p>
            {badge.description && (
              <p className="text-sm text-muted-foreground">{badge.description}</p>
            )}
            {badge.is_system_badge && (
              <p className="text-xs text-muted-foreground italic">System Badge</p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};